import { useEffect, useRef, useState } from "react";
import { api, formatEuro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Bank, UploadSimple, Trash, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import CityCombobox from "@/components/CityCombobox";

export default function NPL() {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/npl${city ? `?city=${city}` : ""}`).then((r) => setItems(r.data));

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
  }, []);
  useEffect(() => { load(); }, [city]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/npl/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importate ${data.inserted} righe`);
      load();
    } catch (err) {
      toast.error("Errore durante l'upload del CSV");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearAll = async () => {
    if (!confirm("Rimuovere tutti i tuoi record NPL?")) return;
    await api.delete("/npl");
    toast.success("Dataset NPL svuotato");
    load();
  };

  const downloadTemplate = () => {
    const header = "bank,borrower_ref,city,zone,address,property_type,sqm,market_value,npl_value,status,notes";
    const example = "Banca Alfa,BR-12345,milano,Navigli,Via X 10,Appartamento,80,320000,180000,unpaid,Perizia disponibile";
    const csv = header + "\n" + example + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "immoscope-npl-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-data text-xs text-amber-400 tracking-widest mb-2">— NPL / MUTUI INCAGLIATI</div>
          <h1 className="font-display font-black text-3xl mb-2">Portafogli NPL bancari</h1>
          <p className="text-sm text-zinc-400 max-w-3xl leading-relaxed">
            I dati NPL (Non Performing Loans) e mutui in sofferenza <span className="text-zinc-100">non sono pubblici</span>: provengono
            da provider bancari e piattaforme private. Carica qui il tuo dataset (CSV) per analizzarlo insieme alle aste PVP e alle inserzioni.
          </p>

          <div className="mt-6 flex flex-wrap items-end gap-3">
            <div className="min-w-[220px]">
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">FILTRO CITTÀ</Label>
              <div className="mt-1">
                <CityCombobox value={city} onChange={setCity} cities={cities} testId="npl-city" placeholder="Tutte" />
              </div>
            </div>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="rounded-none border-white/20 text-zinc-100 hover:bg-white/5 h-10" data-testid="btn-template">
                <FileArrowDown size={14} weight="bold" className="mr-1.5" />Template CSV
              </Button>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onUpload} data-testid="npl-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-none bg-amber-400 text-amber-950 hover:bg-amber-500 font-bold h-10" data-testid="btn-upload">
                <UploadSimple size={14} weight="bold" className="mr-1.5" />{busy ? "Upload..." : "Carica CSV"}
              </Button>
              {items.length > 0 && (
                <Button variant="ghost" onClick={clearAll} className="rounded-none text-red-400 hover:bg-red-500/10 h-10" data-testid="btn-clear">
                  <Trash size={14} weight="bold" className="mr-1.5" />Svuota
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="border border-white/10" data-testid="npl-list">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-white/10 bg-zinc-900/50 font-mono-data text-[10px] text-zinc-500 tracking-widest">
            <div className="col-span-2">BANCA</div>
            <div className="col-span-2">CITTÀ / ZONA</div>
            <div className="col-span-2">INDIRIZZO</div>
            <div className="col-span-1">M²</div>
            <div className="col-span-2 text-right">VALORE MKT</div>
            <div className="col-span-2 text-right">VALORE NPL</div>
            <div className="col-span-1">STATO</div>
          </div>
          {items.length === 0 ? (
            <div className="p-16 text-center">
              <Bank size={40} weight="bold" className="mx-auto text-zinc-700 mb-4" />
              <div className="font-mono-data text-xs text-zinc-500 mb-2">Nessun record NPL caricato</div>
              <div className="text-sm text-zinc-400 max-w-md mx-auto">
                Scarica il template CSV, compilalo con i tuoi dati NPL/pignoramenti bancari e caricalo qui.
              </div>
            </div>
          ) : (
            items.map((n) => {
              const discount = n.market_value && n.npl_value ? Math.round((1 - n.npl_value / n.market_value) * 100) : null;
              return (
                <div key={n.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] font-mono-data text-sm" data-testid={`npl-row-${n.id}`}>
                  <div className="col-span-2 text-zinc-100">{n.bank || "—"}</div>
                  <div className="col-span-2 text-zinc-400">{n.city || "—"} · {n.zone || "—"}</div>
                  <div className="col-span-2 text-zinc-400 truncate">{n.address || "—"}</div>
                  <div className="col-span-1 text-zinc-400">{n.sqm ?? "—"}</div>
                  <div className="col-span-2 text-right text-zinc-400 line-through">{n.market_value ? formatEuro(n.market_value) : "—"}</div>
                  <div className="col-span-2 text-right text-amber-400 font-bold">
                    {n.npl_value ? formatEuro(n.npl_value) : "—"}
                    {discount != null && <span className="ml-2 text-[10px] text-amber-400/80">-{discount}%</span>}
                  </div>
                  <div className="col-span-1 text-zinc-500 uppercase text-[11px]">{n.status || "—"}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
